/**
 * Keep the /search URL in the one shape core's search block can actually live
 * with. Two jobs, both of them things core cannot do on a route that has no
 * content object behind it.
 *
 * 1. `?SearchableText=…` IS AN INBOUND SEED, NOT THE SEARCH.
 *
 *    It is how everything outside the block hands us a query: the masthead
 *    search box, the links in llms.txt, and any URL a person has pasted to a
 *    colleague. But core reads that bare param at HIGHER priority than the
 *    serialized `query` it writes for itself —
 *
 *      const urlSearchText =
 *        locationSearchData.SearchableText ||
 *        urlQuery.find(({ i }) => i === 'SearchableText')?.v || '';
 *
 *    — and its URL writer (`useHashState.setSearchData`) only ever MERGES keys
 *    into `qs.parse(location.search)`; it never deletes one. So the instant the
 *    user searches, the URL holds BOTH: the seed and the new query. Core reads
 *    that URL, the seed wins, and its effect re-runs the ORIGINAL search. The
 *    measurement, from the backend query log, of typing "welding" on a page
 *    reached with `?SearchableText=safety`:
 *
 *      09:16:03  'welding'  120     <- what the user asked for
 *      09:16:04  'safety'   604     <- core re-running the seed, landing LAST
 *
 *    The results the user sees are whichever response arrives last, which is
 *    the stale one. That is the primary entry path into search — the masthead
 *    box on every page produces exactly this URL.
 *
 *    Deleting the seed after the fact does not fix it: by then core has already
 *    seen the both-params URL and queued the stale search. So the seed is
 *    converted ON ARRIVAL into a `query` row — the block's own vocabulary —
 *    before it can ever be read alongside a query. The term does not change,
 *    only where it is written, so nothing re-fetches and nothing flickers; the
 *    block simply stops having a second, louder opinion about what was searched.
 *
 *    Writing ONLY the SearchableText row is deliberate. `getInitialState`
 *    rebuilds the full query from `data` + the facet state on every render, and
 *    core reads the URL's `query` for exactly two things: this term, and facet
 *    seeding (which also reads the bare `?site=…` style params, so those are
 *    untouched either way). Reproducing the portal_type row here would be
 *    duplicating a list that already has one owner.
 *
 * 2. THE PAGE MUST RESET WHEN THE RESULT SET CHANGES.
 *
 *    Core's `usePagination` does this, but only for a block it can find in
 *    `state.content.data.blocks`; /search is a route rather than a content
 *    object, so `useGetBlockType` comes back null and the reset never fires.
 *    Without it, changing the term while on page 2 lands you on page 2 of the
 *    new search — a different slice of different results, with nothing on
 *    screen to say that is what happened.
 *
 *    The rule is stated on the whole query string rather than on the term:
 *    `page` is the ONLY param that may change without resetting the page.
 *    Everything else — the query, a facet, the sort, the status group, the
 *    operator — changes which rows exist, so page 2 of the old set means
 *    nothing in the new one. The first pass never resets, which is what lets an
 *    inbound `?SearchableText=…&page=3` deep link land where it says it will.
 *
 * `history.replace`, not `push`: this corrects a URL rather than navigating.
 * Back must still go back.
 */
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';

const SEED = 'SearchableText';
// Core compresses `plone.app.querystring.operation` to `paqo` on the way into
// the URL and expands it on the way out (serializeQuery/deserializeQuery, both
// module-private). A row written here has to arrive in the compressed form or
// deserializeQuery hands the block an operation name it cannot resolve.
const CONTAINS = 'paqo.string.contains';

const parseRows = (serialized) => {
  if (!serialized) return [];
  try {
    const rows = JSON.parse(serialized);
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    return []; // a hand-mangled query is not worth crashing the page over
  }
};

/**
 * Everything about the URL that decides WHICH rows exist, `page` excluded.
 * `qs.stringify` sorts its keys, so this is stable regardless of the order
 * core, useSearchControls, or a hand-written link happened to emit them in.
 */
const resultSetIdentity = (params) => {
  const { page, ...rest } = params;
  return qs.stringify(rest);
};

export default function useCanonicalSearchUrl() {
  const location = useLocation();
  const history = useHistory();
  // The identity we last saw. `undefined` is "first pass" — there is nothing to
  // have changed from yet, so an inbound deep link keeps its page.
  const seen = React.useRef(undefined);

  React.useEffect(() => {
    const params = qs.parse(location.search || '');
    let changed = false;

    const seed = Array.isArray(params[SEED]) ? params[SEED][0] : params[SEED];
    if (seed) {
      params.query = JSON.stringify([
        ...parseRows(params.query).filter((row) => row?.i !== SEED),
        { i: SEED, o: CONTAINS, v: seed },
      ]);
      delete params[SEED];
      changed = true;
    }

    const identity = resultSetIdentity(params);
    const previous = seen.current;
    seen.current = identity;

    if (
      previous !== undefined &&
      identity !== previous &&
      params.page !== undefined
    ) {
      delete params.page;
      changed = true;
    }

    // Terminates: the replace below leaves no bare seed to convert, and the
    // re-run it triggers computes the same identity it just recorded.
    if (changed) history.replace({ search: qs.stringify(params) });
  }, [location.search, history]);
}
